/**
 * Preference change cross-tab bridge (portable half) — T12.5.1 (G).
 *
 * React Query caches are per-document, and the realtime stream routes events
 * into the sync ENGINE, not these REST reads — so a preference set in another
 * window/tab never reaches a Deals/Planning tab's caches and the badges go stale
 * until a reload. The fix is a cross-tab signal: every preference mutation posts,
 * and each tab invalidates its badge-input queries on receipt.
 *
 * The actual transport (`BroadcastChannel`) is a BROWSER global, which this
 * package may not reference (CP1 — no platform leakage). So this module holds
 * only the portable pieces:
 *   - a `broadcastPreferenceChange()` the mutations call, which delegates to a
 *     poster the host app registers at startup (no-op until then / on Node);
 *   - `invalidatePreferenceBadgeQueries(qc)`, the shared invalidation the
 *     same-tab onSuccess path and the host's cross-tab listener both call.
 *
 * The host (`apps/web`) provides the BroadcastChannel poster + listener and wires
 * them with `setPreferenceChangePoster` + `invalidatePreferenceBadgeQueries`.
 */

import type { QueryClient } from '@tanstack/react-query';
import { keys } from './hooks/keys';

let poster: (() => void) | null = null;

// The host app registers the real cross-tab poster (a BroadcastChannel publish).
// Until then `broadcastPreferenceChange` is a no-op (Node, tests, pre-init).
export function setPreferenceChangePoster(fn: (() => void) | null): void {
  poster = fn;
}

// Called by every preference mutation on settle — delegates to the registered
// host poster, if any.
export function broadcastPreferenceChange(): void {
  try {
    poster?.();
  } catch {
    // A failing transport must never break the mutation — fail soft.
  }
}

// Invalidate the three badge-input queries. Shared so the same-tab path and the
// host's cross-tab listener stay in lockstep.
export function invalidatePreferenceBadgeQueries(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: keys.households.catalogPreferenceSignals });
  void qc.invalidateQueries({ queryKey: keys.households.brandPreferences });
  void qc.invalidateQueries({ queryKey: keys.households.systemProductPreferences });
}
